import { Chip, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { IBillDocument } from '../../../../shared/services/file-upload/file-upload.model'
import { billLineActivity, billLinePeriod, billLineUsage } from '../../../../shared/utils/bill-upload-utils'

/**
 * The cells of one staged row from a bill upload: which bill it came from, the activity, the usage
 * and the period read off it, its status, and why it needs attention. Shared by the import review
 * step and the upload page, so a row reads the same in both.
 */

export interface IBillLineCells {
  bill: ReactNode
  activity: string
  usage: string
  period: string
  status: ReactNode
  notes: ReactNode
}

const normaliseValidationErrors = (errors: any): any[] => {
  if (Array.isArray(errors)) return errors
  if (typeof errors === 'string') {
    try {
      const parsed = JSON.parse(errors)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  if (errors && typeof errors === 'object') {
    return Object.values(errors).flat().filter(Boolean)
  }
  return []
}

export const buildBillLineCells = (
  line: any,
  documentsById: Record<string, IBillDocument>,
  // The wizard calls a clean row "Accepted" (nothing is imported yet); the upload page says "Ready".
  // Leave it out for rows that are already imported or discarded: they get no status chip.
  readyLabel?: string,
): IBillLineCells => {
  const document = line?.billDocumentId ? documentsById[line.billDocumentId] : undefined
  const errors = normaliseValidationErrors(line?.validationErrors)
  const isPossibleDuplicate = !!document?.duplicateOfBillDocumentId
  const noteLines: string[] = errors.map((error: any) => {
    const text = error.message || 'Validation failed'
    return text.length > 140 ? `${text.substring(0, 140)}...` : text
  })
  if (isPossibleDuplicate && readyLabel) {
    noteLines.push(`Possible duplicate of ${document?.duplicateOf?.filename || 'a bill already imported'}. Remove this row if that bill is already recorded.`)
  }

  let status: ReactNode = ''
  if (readyLabel) {
    status = (
      <Stack spacing={0.5} alignItems='flex-start'>
        {errors.length > 0
          ? errors.map((error: any, index: number) => (
            <Chip
              key={index}
              color={error.severity === 'warning' ? 'warning' : 'error'}
              label={error.severity === 'warning' ? 'Warning' : 'Error'}
              size='small'
              variant='outlined'
            />
          ))
          : (
            <Chip
              color={line?.commitStatus === 'FAILED' ? 'error' : 'success'}
              label={line?.commitStatus === 'FAILED' ? 'Failed' : readyLabel}
              size='small'
              variant='outlined'
            />
          )}
        {isPossibleDuplicate && <Chip color='info' label='Possible duplicate' size='small' variant='outlined' />}
      </Stack>
    )
  }

  return {
    bill: (
      <Typography variant='body2' sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={document?.filename || ''}>
        {document?.filename || ''}
      </Typography>
    ),
    activity: billLineActivity(line),
    usage: billLineUsage(line),
    period: billLinePeriod(line),
    status,
    notes: noteLines.length > 0
      ? (
        <Stack spacing={0.5}>
          {noteLines.map((text, index) => <div key={index}>{text}</div>)}
        </Stack>
      )
      : '',
  }
}
